// The cancellation policy, as a standing section rather than something the
// operator has to go and find.
//
// It sits between "before this move" and "what happens on real records" because
// that is the order the questions get asked: how does a record move forward,
// what stops it being abandoned, and then — for both — what does that do to the
// real thing.
//
// One card per TRACK, not per from-state. They used to be clickable chips, each
// editing its own rule set, and that was wrong twice over: it made the operator
// repeat identical blockers, and a forgotten one made cancellation behave
// differently depending on where the record happened to sit.
//
// The from-states are NOT restated under the heading. They are already readable
// from the state chain above and from the `Reached a given state` threshold, and
// a second line here pushed every row out of line with the card beside it.
import {
  abandonsRecord,
  chainStates,
  type Workflow,
  type WorkflowTransition,
} from '../lib/mockWorkflow';
import { RequirementList } from './RequirementList';

export function CancelConditionsCard({
  workflow,
  referenceLabels,
  onChange,
}: {
  workflow: Workflow;
  referenceLabels: string[];
  onChange: (next: WorkflowTransition) => void;
}) {
  // Only the states cancellation is actually reachable FROM. The Order track
  // has no Completed → Cancelled move, so offering Completed as a threshold
  // would let an operator configure a rule that can never fire — and the card's
  // own subtitle would contradict its picker.
  const cancellable = chainStates(workflow).filter((st) =>
    workflow.transitions.some((t) => t.from === st.key && abandonsRecord(workflow, t)),
  );

  if (cancellable.length === 0) return null;

  // Synthetic: only `requirements` carries meaning, because the policy belongs
  // to the track. `onChange` lands it on the workflow, not on a transition.
  const synthetic: WorkflowTransition = {
    from: '',
    to: '',
    setBy: 'operator',
    requirements: workflow.cancelRequirements,
  };

  return (
    <RequirementList
      subject={workflow.subject}
      title="Cancel conditions"
      states={cancellable.map((st) => ({ key: st.key, label: st.label }))}
      referenceLabels={referenceLabels}
      abandons
      transition={synthetic}
      onChange={onChange}
    />
  );
}
